import { Component, inject, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { GoalSettingScorecardTypeEnum, GoalSettingScorecardTypeEnumTypes, MonitoringGoalSettingCardStatusEnum, MonitoringGoalSettingCardStatusEnumTypes, MonitoringGoalSettingProposalStatusEnum, MonitoringGoalSettingProposalStatusEnumTypes, MonitoringGoalSettingSummary, MonitoringGoalSettingUpdateFilterEnum, MonitoringGoalSettingUpdateFilterEnumTypes, MonitoringGoalSettingUserFiltersValues, MonitoringGoalSettingUserSortEnum, MonitoringGoalSettingUserSortEnumTypes, MonitoringGoalSettingUserSummary } from "alloy-performance-engine-cl";
import { MenuItem } from "primeng/api";
import { BaseComponent } from "src/ancestors/base/base.component";
import { TRANSLATION_KEYS } from "src/app/constants/translations/translation-keys";
import { UrlCollection } from "src/app/constants/url-collection";
import { UserMenuItem } from "src/app/constants/utils-type";
import { StatusCardData } from "src/app/components/ape-status-card/ape-status-card.component";
import { ApeDownloadButtonService } from "src/app/components/ape-download-button/ape-download-button.service";
import { ApeModalComponent } from "src/app/components/ape-modal/ape-modal.component";
import { TranslatePipe } from "src/app/pipe/translation.filter.pipe";
import { AlertService } from "src/app/services/alert.service";
import { MonitoringBackofficeApiService } from "src/app/services/api/monitoring-backoffice-api.service";
import { MonitoringGoalSettingBackofficeApiService, MonitoringGoalSettingQueryParams } from "src/app/services/api/monitoring-goal-setting-backoffice-api.service";
import { ImpersonationService } from "src/app/services/impersonation.service";
import { GoalSettingAppliedChip, GoalSettingAppliedChipType, ProcessMonitoringGoalSettingDetailsTemplateComponent } from "src/app/templates/process-monitoring-goal-setting-details-template/process-monitoring-goal-setting-details-template.component";

/**
 * Pagina di monitoraggio Goal Setting (sotto-componente delegato da AdminMonitoringDetailsComponent
 * per i processi di tipo GOAL_SETTING).
 *
 * Responsabilità:
 * - Caricamento KPI, lista filtri disponibili, lista utenti paginata + count
 * - Gestione stato di search/sort/filtri (con persistenza in sessionStorage)
 * - Composizione del layout (counters + toolbar + tabella + drawer + paginazione)
 */
@Component({
  selector: "app-admin-monitoring-goal-setting-details",
  standalone: true,
  imports: [
    CommonModule,
    ProcessMonitoringGoalSettingDetailsTemplateComponent,
    ApeModalComponent,
    TranslatePipe
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: "./monitoring-goal-setting-details.component.html"
})
export class AdminMonitoringGoalSettingDetailsComponent extends BaseComponent implements OnInit {
  readonly TRANSLATION_KEYS = TRANSLATION_KEYS;

  private static readonly SEARCHED_TEXT_MAX_LEN = 200;

  private static readonly SESSION_PAYLOAD_MAX_LEN = 64 * 1024;

  private static readonly SESSION_ARRAY_MAX_LEN = 200;

  static readonly SESSION_KEYS = {
    SEARCHED_TEXT: "monitoringGSDetails_searchedText",
    PAGE: "monitoringGSDetails_page",
    PAGE_SIZE: "monitoringGSDetails_pageSize",
    SORT: "monitoringGSDetails_sort",
    SELECTED_CARD_STATUSES: "monitoringGSDetails_selectedCardStatuses",
    APPLIED_CARD_STATUSES: "monitoringGSDetails_appliedCardStatuses",
    SELECTED_PROPOSAL_STATUSES: "monitoringGSDetails_selectedProposalStatuses",
    APPLIED_PROPOSAL_STATUSES: "monitoringGSDetails_appliedProposalStatuses",
    SELECTED_UPDATES: "monitoringGSDetails_selectedUpdates",
    APPLIED_UPDATES: "monitoringGSDetails_appliedUpdates",
    SELECTED_SCORECARD_TYPES: "monitoringGSDetails_selectedScorecardTypes",
    APPLIED_SCORECARD_TYPES: "monitoringGSDetails_appliedScorecardTypes",
    SELECTED_MANAGERS: "monitoringGSDetails_selectedManagers",
    APPLIED_MANAGERS: "monitoringGSDetails_appliedManagers",
    SELECTED_DEPARTMENTS: "monitoringGSDetails_selectedDepartments",
    APPLIED_DEPARTMENTS: "monitoringGSDetails_appliedDepartments"
  } as const;

  private apiService = inject(MonitoringGoalSettingBackofficeApiService);

  private monitoringBackofficeApiService = inject(MonitoringBackofficeApiService);

  private impersonationService = inject(ImpersonationService);

  private downloadService = inject(ApeDownloadButtonService);

  private alertService = inject(AlertService);

  @Input() processId: string | null = null;

  processName: string = "";

  loading: boolean = true;

  loadingUsers: boolean = false;

  loadingFilters: boolean = false;

  summary: MonitoringGoalSettingSummary | null = null;

  usersData: MonitoringGoalSettingUserSummary[] = [];

  usersDataCount: number = 0;

  filtersValues: MonitoringGoalSettingUserFiltersValues | null = null;

  searchedText: string = "";

  page: number = 1;

  pageSize: number = 10;

  sortOptions: MenuItem[] = [];

  selectedSortOption: MenuItem | undefined = undefined;

  countersCardData: StatusCardData[] = [];

  drawerVisible: boolean = false;

  // Opzioni MenuItem (per dropdown e drawer) — calcolate da filtersValues
  cardStatusOptions: MenuItem[] = [];

  proposalStatusOptions: MenuItem[] = [];

  updatesOptions: MenuItem[] = [];

  scorecardTypeOptions: MenuItem[] = [];

  managerOptions: UserMenuItem[] = [];

  departmentOptions: MenuItem[] = [];

  // Selezione drawer (in-flight: l'utente sta modificando ma non ha ancora applicato)
  selectedCardStatuses: MenuItem[] = [];

  selectedProposalStatuses: MenuItem[] = [];

  selectedUpdates: MenuItem[] = [];

  selectedScorecardTypes: MenuItem[] = [];

  selectedManagers: UserMenuItem[] = [];

  selectedDepartments: MenuItem[] = [];

  // Selezione applicata (effettivamente attiva sulle query)
  appliedCardStatuses: MenuItem[] = [];

  appliedProposalStatuses: MenuItem[] = [];

  appliedUpdates: MenuItem[] = [];

  appliedScorecardTypes: MenuItem[] = [];

  appliedManagers: UserMenuItem[] = [];

  appliedDepartments: MenuItem[] = [];

  /**
   * Stato della modale di conferma sollecito (spec §4.2 e §4.3.2).
   * - `mode`: tipo di sollecito da inviare alla conferma.
   * - `user`: collaboratore target nei casi SINGLE_MANAGER / SINGLE_USER.
   * - `count`: numero di destinatari restituito dalla preview, nei casi BULK_*.
   * - `sending`: true durante la chiamata di invio definitivo, per mostrare loader sul bottone.
   */
  solicitConfirm: {
    visible: boolean;
    mode: "SINGLE_MANAGER" | "SINGLE_USER" | "SINGLE_MANAGER_UPDATE" | "BULK_MANAGERS" | "BULK_USERS" | "BULK_MANAGERS_UPDATE" | null;
    user: MonitoringGoalSettingUserSummary | null;
    count: number;
    sending: boolean;
  } = {
      visible: false,
      mode: null,
      user: null,
      count: 0,
      sending: false
    };

  override async ngOnInit(): Promise<void> {
    if (!this.processId) {
      this.logger.error("processId is required for AdminMonitoringGoalSettingDetailsComponent");
      return;
    }
    try {
      this.sortOptions = [
        {
          id: MonitoringGoalSettingUserSortEnumTypes.SURNAME_ASC,
          label: this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.order.SURNAME_ASC)
        },
        {
          id: MonitoringGoalSettingUserSortEnumTypes.SURNAME_DESC,
          label: this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.order.SURNAME_DESC)
        }
      ];
      this.selectedSortOption = this.sortOptions[0];

      await this.loadProcessName();
      await this.loadFilters();
      this.restoreStateFromSession();
      await Promise.all([
        this.loadSummary(),
        this.loadUsersCount(),
        this.loadUsersData()
      ]);
    } catch (err) {
      this.logger.error(`Error initializing GS monitoring details: ${String(err)}`);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Carica il nome del processo per il sottotitolo della pagina.
   */
  private async loadProcessName(): Promise<void> {
    if (!this.processId) return;
    try {
      const process = await this.monitoringBackofficeApiService.getProcess(this.processId);
      this.processName = process?.name || "";
    } catch (err) {
      this.logger.warn(`Error loading process name: ${String(err)}`);
    }
  }

  goBack(): void {
    void this.router.navigate([UrlCollection.adminMonitoring]);
  }

  /**
   * Carica le opzioni dei filtri (valori disponibili per il drawer e per le chip).
   */
  private async loadFilters(): Promise<void> {
    if (!this.processId) return;
    try {
      this.loadingFilters = true;
      const values = await this.apiService.getUserFiltersValues(this.processId);
      this.filtersValues = values;

      this.cardStatusOptions = (values.cardStatus || []).map(s => ({
        id: s,
        label: this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.cardStatus[s])
      }));

      this.proposalStatusOptions = (values.proposalStatus || []).map(s => ({
        id: s,
        label: this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.proposalStatus[s])
      }));

      this.updatesOptions = (values.updates || []).map(u => ({
        id: u,
        label: this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.updates[u])
      }));

      this.scorecardTypeOptions = (values.scorecardType || []).map(s => ({
        id: s,
        label: this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.scorecardType[s])
      }));

      this.managerOptions = (values.evaluators || []).map(e => ({
        id: e.userId,
        label: `${e.surname ?? ""} ${e.forename ?? ""}`.trim(),
        forename: e.forename,
        surname: e.surname,
        email: e.email,
        role: ""
      }));

      this.departmentOptions = (values.department || []).map(d => ({
        id: d,
        label: d
      }));
    } finally {
      this.loadingFilters = false;
    }
  }

  private buildQueryParams(): MonitoringGoalSettingQueryParams {
    return {
      searchedText: this.searchedText?.trim() || undefined,
      cardStatus: this.appliedCardStatuses.length > 0
        ? this.appliedCardStatuses.map(o => o.id as MonitoringGoalSettingCardStatusEnum)
        : undefined,
      proposalStatus: this.appliedProposalStatuses.length > 0
        ? this.appliedProposalStatuses.map(o => o.id as MonitoringGoalSettingProposalStatusEnum)
        : undefined,
      updates: this.appliedUpdates.length > 0
        ? this.appliedUpdates.map(o => o.id as MonitoringGoalSettingUpdateFilterEnum)
        : undefined,
      scorecardType: this.appliedScorecardTypes.length > 0
        ? this.appliedScorecardTypes.map(o => o.id as GoalSettingScorecardTypeEnum)
        : undefined,
      department: this.appliedDepartments.length > 0
        ? this.appliedDepartments.map(o => o.id as string)
        : undefined,
      evaluators: this.appliedManagers.length > 0
        ? this.appliedManagers.map(o => o.id as string)
        : undefined
    };
  }

  private async loadSummary(): Promise<void> {
    if (!this.processId) return;
    try {
      this.summary = await this.apiService.getProcessSummary(this.processId, this.buildQueryParams());
      this.refreshCountersCardData();
    } catch (err) {
      this.logger.error(`Error loading GS summary: ${String(err)}`);
    }
  }

  private async loadUsersCount(): Promise<void> {
    if (!this.processId) return;
    try {
      this.usersDataCount = await this.apiService.getUsersDataCount(this.processId, this.buildQueryParams());
    } catch (err) {
      this.logger.error(`Error loading GS users count: ${String(err)}`);
    }
  }

  private async loadUsersData(): Promise<void> {
    if (!this.processId) return;
    try {
      this.loadingUsers = true;
      const sortBy = (this.selectedSortOption?.id as MonitoringGoalSettingUserSortEnum) || MonitoringGoalSettingUserSortEnumTypes.SURNAME_ASC;
      this.usersData = await this.apiService.getUsersData(this.processId, {
        ...this.buildQueryParams(),
        fromRecord: (this.page - 1) * this.pageSize,
        numRecords: this.pageSize,
        sortBy
      });
    } catch (err) {
      this.logger.error(`Error loading GS users data: ${String(err)}`);
    } finally {
      this.loadingUsers = false;
    }
  }

  /**
   * Costruisce i 3 KPI in testa pagina dal summary corrente.
   * Pattern allineato a MonitoringDetailsComponent.formatMonitoringSummaryDataEvaluation:
   * solo testo+icona+badge percentuale, niente gauge (la proprietà completeness farebbe
   * commutare ape-status-card sul rendering del gauge SVG).
   */
  private refreshCountersCardData(): void {
    if (!this.summary) {
      this.countersCardData = [];
      return;
    }
    const totIncluded = this.summary.totIncludedUsers ?? 0;
    const completedDefinitions = this.summary.completedDefinitions ?? 0;
    const completedProposals = this.summary.completedProposals ?? 0;
    const totEligibleProposals = this.summary.totEligibleProposals ?? 0;
    const definitionsPercentage = totIncluded > 0
      ? ((completedDefinitions / totIncluded) * 100).toFixed(2)
      : "0";
    const proposalsValue = this.summary.isUserProposalPhaseEnabled
      ? `${completedProposals.toLocaleString("it-IT")}/${totEligibleProposals.toLocaleString("it-IT")}`
      : String(this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.kpi.SENT_PROPOSALS_NOT_AVAILABLE));
    const proposalsPercentage = this.summary.isUserProposalPhaseEnabled && totEligibleProposals > 0
      ? ((completedProposals / totEligibleProposals) * 100).toFixed(2)
      : undefined;
    this.countersCardData = [
      {
        title: this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.kpi.INCLUDED_ON_PROCESS),
        description: this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.kpi.INCLUDED_ON_PROCESS_DESCRIPTION),
        value: totIncluded.toLocaleString("it-IT"),
        icon: "assets/icons/user-round-plus.svg",
        withTooltip: true,
        totEvaluations: totIncluded,
        scorecardsOffice: this.summary.totIncludedOfficeUsers ?? 0,
        scorecardsOperations: this.summary.totIncludedOperationsUsers ?? 0,
        scorecardsManager: this.summary.totIncludedManagerUsers ?? 0
      },
      {
        title: this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.kpi.SHARED_CARDS),
        description: this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.kpi.SHARED_CARDS_DESCRIPTION),
        value: `${completedDefinitions.toLocaleString("it-IT")}/${totIncluded.toLocaleString("it-IT")}`,
        icon: "assets/icons/target.svg",
        percentage: definitionsPercentage
      },
      {
        title: this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.kpi.SENT_PROPOSALS),
        description: this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.kpi.SENT_PROPOSALS_DESCRIPTION),
        value: proposalsValue,
        icon: "assets/icons/clipboard-list.svg",
        percentage: proposalsPercentage
      }
    ];
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.usersDataCount / this.pageSize));
  }

  get activeFiltersCount(): number {
    return this.appliedCardStatuses.length
      + this.appliedProposalStatuses.length
      + this.appliedUpdates.length
      + this.appliedScorecardTypes.length
      + this.appliedManagers.length
      + this.appliedDepartments.length;
  }

  // -- Handlers UI --

  onSearchChange(value: string): void {
    this.searchedText = value || "";
    this.persistSession();
  }

  async onSearchUsers(): Promise<void> {
    this.page = 1;
    await this.reloadAll();
  }

  async onSortChange(option: MenuItem): Promise<void> {
    this.selectedSortOption = option;
    this.page = 1;
    sessionStorage.setItem(AdminMonitoringGoalSettingDetailsComponent.SESSION_KEYS.SORT, String(option?.id ?? ""));
    await this.loadUsersData();
  }

  async onPageChange(page: number): Promise<void> {
    this.page = page;
    sessionStorage.setItem(AdminMonitoringGoalSettingDetailsComponent.SESSION_KEYS.PAGE, String(page));
    await this.loadUsersData();
  }

  async onPageSizeChange(pageSize: number): Promise<void> {
    this.pageSize = pageSize;
    this.page = 1;
    sessionStorage.setItem(AdminMonitoringGoalSettingDetailsComponent.SESSION_KEYS.PAGE_SIZE, String(pageSize));
    await this.loadUsersData();
  }

  onAdvancedFiltersClick(): void {
    // Riallinea le selezioni del drawer con i filtri applicati
    this.selectedCardStatuses = [...this.appliedCardStatuses];
    this.selectedProposalStatuses = [...this.appliedProposalStatuses];
    this.selectedUpdates = [...this.appliedUpdates];
    this.selectedScorecardTypes = [...this.appliedScorecardTypes];
    this.selectedManagers = [...this.appliedManagers];
    this.selectedDepartments = [...this.appliedDepartments];
    this.drawerVisible = true;
  }

  /**
   * Pulsante toolbar "Sollecita condivisione obiettivi" (bulk M4).
   * Spec §4.2: prima di confermare l'amministratore visualizza un riepilogo del numero
   * di destinatari coinvolti. Faccio quindi una chiamata di preview e apro la modale.
   */
  async onSolicitDefinitionsClicked(): Promise<void> {
    if (!this.processId) return;
    try {
      const count = await this.monitoringBackofficeApiService.solicitAllGoalSettingManagerDefinitions(this.processId, true);
      if (!count) {
        // nessun destinatario eleggibile — mostro un info toast invece di aprire la modale
        this.alertService.showInfo(this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.solicit.BULK_NO_RECIPIENTS));
        return;
      }
      this.solicitConfirm = {
        visible: true,
        mode: "BULK_MANAGERS",
        user: null,
        count,
        sending: false
      };
    } catch (err) {
      this.logger.error(`[GS monitoring] preview bulk solicit managers failed: ${ String(err) }`);
      this.alertService.showError(this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.solicit.ERROR_GENERIC));
    }
  }

  /**
   * Pulsante toolbar "Sollecita proposte" (bulk M3).
   * Stesso flusso di onSolicitDefinitionsClicked, ma per i collaboratori Office con
   * proposte in stato DA FARE.
   */
  async onSolicitProposalsClicked(): Promise<void> {
    if (!this.processId) return;
    try {
      const count = await this.monitoringBackofficeApiService.solicitAllGoalSettingUserProposals(this.processId, true);
      if (!count) {
        this.alertService.showInfo(this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.solicit.BULK_NO_RECIPIENTS));
        return;
      }
      this.solicitConfirm = {
        visible: true,
        mode: "BULK_USERS",
        user: null,
        count,
        sending: false
      };
    } catch (err) {
      this.logger.error(`[GS monitoring] preview bulk solicit employees failed: ${ String(err) }`);
      this.alertService.showError(this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.solicit.ERROR_GENERIC));
    }
  }

  /**
   * Voce di menu "Sollecita manager" (M4 puntuale): apre la modale di conferma.
   * Il flag user.canSolicitManager dal BE garantisce che siamo in condizione valida
   * (processo Attivo, fase Definizione aperta, scheda DA FARE).
   */
  onSolicitManagerClicked(user: MonitoringGoalSettingUserSummary): void {
    this.solicitConfirm = {
      visible: true,
      mode: "SINGLE_MANAGER",
      user,
      count: 0,
      sending: false
    };
  }

  /**
   * Voce di menu "Sollecita collaboratore" (M3 puntuale): apre la modale di conferma.
   * Il flag user.canSolicitUser dal BE garantisce che siamo in condizione valida
   * (processo Attivo, fase Proposta aperta, scheda Office, proposte DA FARE).
   */
  onSolicitUserClicked(user: MonitoringGoalSettingUserSummary): void {
    this.solicitConfirm = {
      visible: true,
      mode: "SINGLE_USER",
      user,
      count: 0,
      sending: false
    };
  }

  /**
   * Voce di menu "Sollecita aggiornamento manager" (M6 puntuale): apre la modale di
   * conferma. Il flag user.canSolicitManagerUpdate dal BE garantisce che siamo in
   * condizione valida (processo Attivo, fase Aggiornamento aperta, scheda non ancora
   * ricondivisa).
   */
  onSolicitManagerUpdateClicked(user: MonitoringGoalSettingUserSummary): void {
    this.solicitConfirm = {
      visible: true,
      mode: "SINGLE_MANAGER_UPDATE",
      user,
      count: 0,
      sending: false
    };
  }

  /**
   * Pulsante toolbar "Sollecita aggiornamento" (bulk M6). Stesso flusso preview di
   * onSolicitDefinitionsClicked, ma per i manager con scheda non ancora ricondivisa
   * nella fase di Aggiornamento.
   */
  async onSolicitUpdatesClicked(): Promise<void> {
    if (!this.processId) return;
    try {
      const count = await this.monitoringBackofficeApiService.solicitAllGoalSettingManagerUpdates(this.processId, true);
      if (!count) {
        this.alertService.showInfo(this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.solicit.BULK_NO_RECIPIENTS));
        return;
      }
      this.solicitConfirm = {
        visible: true,
        mode: "BULK_MANAGERS_UPDATE",
        user: null,
        count,
        sending: false
      };
    } catch (err) {
      this.logger.error(`[GS monitoring] preview bulk solicit manager updates failed: ${ String(err) }`);
      this.alertService.showError(this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.solicit.ERROR_GENERIC));
    }
  }

  /**
   * Conferma della modale: dispatching sulla modalità → chiamata API definitiva →
   * toast di esito → chiusura modale → ricarica dati per riflettere il nuovo stato.
   */
  async confirmSolicit(): Promise<void> {
    if (!this.processId || !this.solicitConfirm.mode) {
      this.cancelSolicit();
      return;
    }
    this.solicitConfirm.sending = true;
    try {
      const labels = TRANSLATION_KEYS.admin.goalSettingMonitoring.solicit;
      switch (this.solicitConfirm.mode) {
        case "SINGLE_MANAGER":
          if (!this.solicitConfirm.user) return;
          await this.monitoringBackofficeApiService.solicitGoalSettingManagerForEmployee(this.processId, this.solicitConfirm.user.userId);
          this.alertService.showSuccess(this.translateService.instant(labels.SUCCESS_SINGLE_MANAGER));
          break;
        case "SINGLE_USER":
          if (!this.solicitConfirm.user) return;
          await this.monitoringBackofficeApiService.solicitGoalSettingEmployeeProposal(this.processId, this.solicitConfirm.user.userId);
          this.alertService.showSuccess(this.translateService.instant(labels.SUCCESS_SINGLE_USER));
          break;
        case "SINGLE_MANAGER_UPDATE":
          if (!this.solicitConfirm.user) return;
          await this.monitoringBackofficeApiService.solicitGoalSettingManagerUpdateForEmployee(this.processId, this.solicitConfirm.user.userId);
          this.alertService.showSuccess(this.translateService.instant(labels.SUCCESS_SINGLE_MANAGER_UPDATE));
          break;
        case "BULK_MANAGERS":
          await this.monitoringBackofficeApiService.solicitAllGoalSettingManagerDefinitions(this.processId, false);
          this.alertService.showSuccess(this.translateService.instant(labels.SUCCESS_BULK_MANAGERS));
          break;
        case "BULK_USERS":
          await this.monitoringBackofficeApiService.solicitAllGoalSettingUserProposals(this.processId, false);
          this.alertService.showSuccess(this.translateService.instant(labels.SUCCESS_BULK_USERS));
          break;
        case "BULK_MANAGERS_UPDATE":
          await this.monitoringBackofficeApiService.solicitAllGoalSettingManagerUpdates(this.processId, false);
          this.alertService.showSuccess(this.translateService.instant(labels.SUCCESS_BULK_MANAGERS_UPDATE));
          break;
      }
      this.cancelSolicit();
      // ricarico la lista utenti per riflettere eventuali transizioni di stato sui flag canSolicit*
      await Promise.all([
        this.loadSummary(),
        this.loadUsersCount(),
        this.loadUsersData()
      ]);
    } catch (err) {
      this.logger.error(`[GS monitoring] solicit confirm failed (${ this.solicitConfirm.mode }): ${ String(err) }`);
      this.alertService.showError(this.translateService.instant(TRANSLATION_KEYS.admin.goalSettingMonitoring.solicit.ERROR_GENERIC));
      this.solicitConfirm.sending = false;
    }
  }

  /** Chiude la modale di conferma sollecito senza inviare nulla. */
  cancelSolicit(): void {
    this.solicitConfirm = {
      visible: false,
      mode: null,
      user: null,
      count: 0,
      sending: false
    };
  }

  /**
   * Titolo (già tradotto + placeholder sostituiti) del modale di sollecito.
   * I titoli dei 3 SINGLE_* contengono {{forename}}/{{surname}} del collaboratore selezionato;
   * i 3 BULK_* non hanno placeholder ma passano dallo stesso meccanismo per uniformità.
   */
  get solicitModalTitle(): string {
    const k = TRANSLATION_KEYS.admin.goalSettingMonitoring.solicit;
    const user = this.solicitConfirm.user;
    const forename = user?.forename || "";
    const surname = user?.surname || "";
    switch (this.solicitConfirm.mode) {
      case "SINGLE_MANAGER":
        return this.translateService.instant<string>(k.SINGLE_MANAGER_TITLE)
          .replace("{{forename}}", forename)
          .replace("{{surname}}", surname);
      case "SINGLE_USER":
        return this.translateService.instant<string>(k.SINGLE_USER_TITLE)
          .replace("{{forename}}", forename)
          .replace("{{surname}}", surname);
      case "SINGLE_MANAGER_UPDATE":
        return this.translateService.instant<string>(k.SINGLE_MANAGER_UPDATE_TITLE)
          .replace("{{forename}}", forename)
          .replace("{{surname}}", surname);
      case "BULK_MANAGERS":
        return this.translateService.instant<string>(k.BULK_MANAGERS_TITLE);
      case "BULK_USERS":
        return this.translateService.instant<string>(k.BULK_USERS_TITLE);
      case "BULK_MANAGERS_UPDATE":
        return this.translateService.instant<string>(k.BULK_MANAGERS_UPDATE_TITLE);
      default:
        return "";
    }
  }

  /**
   * Descrizione (già tradotta + placeholder sostituiti) del modale di sollecito.
   * I placeholder supportati sono {{forename}}/{{surname}} per i singoli e {{count}} per i bulk.
   */
  get solicitModalDescription(): string {
    const k = TRANSLATION_KEYS.admin.goalSettingMonitoring.solicit;
    const user = this.solicitConfirm.user;
    const forename = user?.forename || "";
    const surname = user?.surname || "";
    const count = this.solicitConfirm.count.toString();
    switch (this.solicitConfirm.mode) {
      case "SINGLE_MANAGER":
        return this.translateService.instant<string>(k.SINGLE_MANAGER_DESCRIPTION)
          .replace("{{forename}}", forename)
          .replace("{{surname}}", surname);
      case "SINGLE_USER":
        return this.translateService.instant<string>(k.SINGLE_USER_DESCRIPTION)
          .replace("{{forename}}", forename)
          .replace("{{surname}}", surname);
      case "SINGLE_MANAGER_UPDATE":
        return this.translateService.instant<string>(k.SINGLE_MANAGER_UPDATE_DESCRIPTION)
          .replace("{{forename}}", forename)
          .replace("{{surname}}", surname);
      case "BULK_MANAGERS":
        return this.translateService.instant<string>(k.BULK_MANAGERS_DESCRIPTION)
          .replace("{{count}}", count);
      case "BULK_USERS":
        return this.translateService.instant<string>(k.BULK_USERS_DESCRIPTION)
          .replace("{{count}}", count);
      case "BULK_MANAGERS_UPDATE":
        return this.translateService.instant<string>(k.BULK_MANAGERS_UPDATE_DESCRIPTION)
          .replace("{{count}}", count);
      default:
        return "";
    }
  }

  /**
   * Esporta in Excel il report di monitoraggio del processo Goal Setting corrente.
   * Il loader sul pulsante e il toast di successo sono gestiti da ApeDownloadButtonComponent
   * tramite ApeDownloadButtonService: il completamento del download (anche in caso di errore)
   * viene segnalato chiamando completeDownload(downloadId) nel finally.
   */
  async onExportXlsClicked(downloadId: string): Promise<void> {
    try {
      if (!this.processId) {
        this.logger.error("Process ID is required for GS monitoring export");
        return;
      }
      await this.apiService.exportGoalSettingMonitoring(this.processId, downloadId);
    } catch (err) {
      this.logger.error(`Error exporting GS monitoring report: ${String(err)}`);
    } finally {
      this.downloadService.completeDownload(downloadId);
    }
  }

  /**
   * Impersonifica l'utente selezionato dal menu azioni di riga.
   * Riusa la stessa logica della pagina admin/impersonate.
   */
  async onImpersonateUser(user: MonitoringGoalSettingUserSummary): Promise<void> {
    if (!this.processId) return;
    this.persistSession();
    await this.impersonationService.handleImpersonation(user, user.userId, {
      path: UrlCollection.adminMonitoring,
      processId: this.processId
    });
  }

  /**
   * Scarica il PDF della scheda obiettivi dell'utente selezionato dal menu azioni di riga.
   * Il loader e il toast del pulsante non sono visualizzati (il bottone è una voce di menu, non
   * un ape-download-button), ma usiamo comunque ApeDownloadButtonService per coerenza col
   * pattern dell'export Excel (e per gestire correttamente l'isDownloadValid lato API service).
   */
  async onDownloadPdf(user: MonitoringGoalSettingUserSummary): Promise<void> {
    if (!this.processId) return;
    const downloadId = this.downloadService.startDownload();
    try {
      await this.apiService.downloadGoalSettingUserScorecardPdf(this.processId, user.userId, downloadId);
    } catch (err) {
      this.logger.error(`Error downloading GS scorecard PDF: ${String(err)}`);
    }
  }

  /**
   * Impersonifica il manager dell'utente selezionato.
   * Riusa la stessa logica della pagina admin/impersonate.
   */
  async onImpersonateManager(user: MonitoringGoalSettingUserSummary): Promise<void> {
    if (!this.processId) return;
    if (!user.impersonableManagerId) {
      this.logger.error(`Manager ID not available for user ${user.userId}`);
      return;
    }
    this.persistSession();
    await this.impersonationService.handleImpersonation(user, user.impersonableManagerId, {
      path: UrlCollection.adminMonitoring,
      processId: this.processId
    });
  }

  closeDrawer(): void {
    this.drawerVisible = false;
  }

  async applyFilters(): Promise<void> {
    this.appliedCardStatuses = [...this.selectedCardStatuses];
    this.appliedProposalStatuses = [...this.selectedProposalStatuses];
    this.appliedUpdates = [...this.selectedUpdates];
    this.appliedScorecardTypes = [...this.selectedScorecardTypes];
    this.appliedManagers = [...this.selectedManagers];
    this.appliedDepartments = [...this.selectedDepartments];
    this.page = 1;
    this.persistSession();
    this.drawerVisible = false;
    await this.reloadAll();
  }

  async resetFilters(): Promise<void> {
    this.selectedCardStatuses = [];
    this.selectedProposalStatuses = [];
    this.selectedUpdates = [];
    this.selectedScorecardTypes = [];
    this.selectedManagers = [];
    this.selectedDepartments = [];
    this.appliedCardStatuses = [];
    this.appliedProposalStatuses = [];
    this.appliedUpdates = [];
    this.appliedScorecardTypes = [];
    this.appliedManagers = [];
    this.appliedDepartments = [];
    this.page = 1;
    this.persistSession();
    this.drawerVisible = false;
    await this.reloadAll();
  }

  /**
   * Rimuove un singolo chip filtro dalla pagina (sotto la search bar).
   */
  async onRemoveFilterChip(filter: {
    id: string;
    type: GoalSettingAppliedChipType;
  }): Promise<void> {
    const apply = (arr: (MenuItem | UserMenuItem)[]) => arr.filter(o => o.id !== filter.id) as MenuItem[];
    switch (filter.type) {
      case "APPLIED_CARD_STATUSES":
        this.appliedCardStatuses = apply(this.appliedCardStatuses);
        this.selectedCardStatuses = [...this.appliedCardStatuses];
        break;
      case "APPLIED_PROPOSAL_STATUSES":
        this.appliedProposalStatuses = apply(this.appliedProposalStatuses);
        this.selectedProposalStatuses = [...this.appliedProposalStatuses];
        break;
      case "APPLIED_UPDATES":
        this.appliedUpdates = apply(this.appliedUpdates);
        this.selectedUpdates = [...this.appliedUpdates];
        break;
      case "APPLIED_SCORECARD_TYPES":
        this.appliedScorecardTypes = apply(this.appliedScorecardTypes);
        this.selectedScorecardTypes = [...this.appliedScorecardTypes];
        break;
      case "APPLIED_MANAGERS":
        this.appliedManagers = apply(this.appliedManagers) as UserMenuItem[];
        this.selectedManagers = [...this.appliedManagers];
        break;
      case "APPLIED_DEPARTMENTS":
        this.appliedDepartments = apply(this.appliedDepartments);
        this.selectedDepartments = [...this.appliedDepartments];
        break;
    }
    this.page = 1;
    this.persistSession();
    await this.reloadAll();
  }

  private async reloadAll(): Promise<void> {
    await Promise.all([
      this.loadSummary(),
      this.loadUsersCount(),
      this.loadUsersData()
    ]);
  }

  private persistSession(): void {
    const k = AdminMonitoringGoalSettingDetailsComponent.SESSION_KEYS;
    sessionStorage.setItem(k.SEARCHED_TEXT, this.searchedText);
    sessionStorage.setItem(k.PAGE, String(this.page));
    sessionStorage.setItem(k.PAGE_SIZE, String(this.pageSize));
    sessionStorage.setItem(k.APPLIED_CARD_STATUSES, JSON.stringify(this.appliedCardStatuses));
    sessionStorage.setItem(k.APPLIED_PROPOSAL_STATUSES, JSON.stringify(this.appliedProposalStatuses));
    sessionStorage.setItem(k.APPLIED_UPDATES, JSON.stringify(this.appliedUpdates));
    sessionStorage.setItem(k.APPLIED_SCORECARD_TYPES, JSON.stringify(this.appliedScorecardTypes));
    sessionStorage.setItem(k.APPLIED_MANAGERS, JSON.stringify(this.appliedManagers));
    sessionStorage.setItem(k.APPLIED_DEPARTMENTS, JSON.stringify(this.appliedDepartments));
  }

  private restoreStateFromSession(): void {
    const k = AdminMonitoringGoalSettingDetailsComponent.SESSION_KEYS;
    try {
      this.searchedText = (sessionStorage.getItem(k.SEARCHED_TEXT) ?? "").slice(0, AdminMonitoringGoalSettingDetailsComponent.SEARCHED_TEXT_MAX_LEN);
      const pageRaw = sessionStorage.getItem(k.PAGE);
      if (pageRaw) this.page = Number(pageRaw) || 1;
      const pageSizeRaw = sessionStorage.getItem(k.PAGE_SIZE);
      if (pageSizeRaw) this.pageSize = Number(pageSizeRaw) || 10;
      const sortRaw = sessionStorage.getItem(k.SORT);
      if (sortRaw) {
        this.selectedSortOption = this.sortOptions.find(o => o.id === sortRaw) ?? this.sortOptions[0];
      }
      this.appliedCardStatuses = this.safeParse<MenuItem[]>(sessionStorage.getItem(k.APPLIED_CARD_STATUSES)) || [];
      this.appliedProposalStatuses = this.safeParse<MenuItem[]>(sessionStorage.getItem(k.APPLIED_PROPOSAL_STATUSES)) || [];
      this.appliedUpdates = this.safeParse<MenuItem[]>(sessionStorage.getItem(k.APPLIED_UPDATES)) || [];
      this.appliedScorecardTypes = this.safeParse<MenuItem[]>(sessionStorage.getItem(k.APPLIED_SCORECARD_TYPES)) || [];
      this.appliedManagers = this.safeParse<UserMenuItem[]>(sessionStorage.getItem(k.APPLIED_MANAGERS)) || [];
      this.appliedDepartments = this.safeParse<MenuItem[]>(sessionStorage.getItem(k.APPLIED_DEPARTMENTS)) || [];
    } catch (err) {
      this.logger.warn(`Error restoring GS monitoring session: ${String(err)}`);
    }
  }

  /**
   * Parser difensivo per i valori serializzati in sessionStorage.
   * - Reviver: scarta __proto__/constructor/prototype (prototype pollution).
   * - Validazione: solo array di oggetti piatti con id/label stringa opzionali,
   *   lunghezza max limitata.
   */
  private safeParse<T>(raw: string | null): T | null {
    if (!raw) return null;
    if (raw.length > AdminMonitoringGoalSettingDetailsComponent.SESSION_PAYLOAD_MAX_LEN) return null;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw, (key: string, value: unknown) => {
        if (key === "__proto__" || key === "constructor" || key === "prototype") return undefined;
        return value;
      });
    } catch {
      return null;
    }
    if (!Array.isArray(parsed)) return null;
    if (parsed.length > AdminMonitoringGoalSettingDetailsComponent.SESSION_ARRAY_MAX_LEN) return null;
    for (const item of parsed) {
      if (item === null || typeof item !== "object" || Array.isArray(item)) return null;
      const i = item as Record<string, unknown>;
      if (i.id !== undefined && typeof i.id !== "string") return null;
      if (i.label !== undefined && typeof i.label !== "string") return null;
    }
    return parsed as unknown as T;
  }

  // Helpers per la lista di chip "filtri applicati" sotto la search bar
  get appliedChips(): GoalSettingAppliedChip[] {
    const chips: GoalSettingAppliedChip[] = [];
    this.appliedCardStatuses.forEach(o => chips.push({
      id: o.id ?? "",
      label: String(o.label ?? ""),
      type: "APPLIED_CARD_STATUSES"
    }));
    this.appliedProposalStatuses.forEach(o => chips.push({
      id: o.id ?? "",
      label: String(o.label ?? ""),
      type: "APPLIED_PROPOSAL_STATUSES"
    }));
    this.appliedUpdates.forEach(o => chips.push({
      id: o.id ?? "",
      label: String(o.label ?? ""),
      type: "APPLIED_UPDATES"
    }));
    this.appliedScorecardTypes.forEach(o => chips.push({
      id: o.id ?? "",
      label: String(o.label ?? ""),
      type: "APPLIED_SCORECARD_TYPES"
    }));
    this.appliedManagers.forEach(o => chips.push({
      id: o.id ?? "",
      label: String(o.label ?? ""),
      type: "APPLIED_MANAGERS"
    }));
    this.appliedDepartments.forEach(o => chips.push({
      id: o.id ?? "",
      label: String(o.label ?? ""),
      type: "APPLIED_DEPARTMENTS"
    }));
    return chips;
  }

  // -- bridges per il drawer (in-flight selection) --
  onCardStatusesChange(selected: MenuItem[]): void {
    this.selectedCardStatuses = selected;
  }

  onProposalStatusesChange(selected: MenuItem[]): void {
    this.selectedProposalStatuses = selected;
  }

  onUpdatesChange(selected: MenuItem[]): void {
    this.selectedUpdates = selected;
  }

  onScorecardTypesChange(selected: MenuItem[]): void {
    this.selectedScorecardTypes = selected;
  }

  onManagersChange(selected: UserMenuItem[]): void {
    this.selectedManagers = selected;
  }

  onDepartmentsChange(selected: MenuItem[]): void {
    this.selectedDepartments = selected;
  }

  protected readonly GoalSettingScorecardTypeEnumTypes = GoalSettingScorecardTypeEnumTypes;

  protected readonly MonitoringGoalSettingCardStatusEnumTypes = MonitoringGoalSettingCardStatusEnumTypes;

  protected readonly MonitoringGoalSettingProposalStatusEnumTypes = MonitoringGoalSettingProposalStatusEnumTypes;

  protected readonly MonitoringGoalSettingUpdateFilterEnumTypes = MonitoringGoalSettingUpdateFilterEnumTypes;
}
